import type { BuiltForm, FieldType, FormField } from '../models/built-form';
import { hydrateGridChoices } from './grid-choice-option';
import {
  answerFields,
  type AnswerValue,
  type RespondentAnswers,
} from './respondent-answer-form';

export type SnapshotChoice = {
  id: string;
  label: string;
};

export type ApiAnswerItem = {
  fieldId: string;
  value: unknown;
};

const FILE_FIELD_TYPES = new Set<FieldType>([
  'file_uploader',
  'image_picker',
  'camera',
  'audio_recorder',
  'video_recorder',
  'document_scanner',
  'drawing',
  'signature',
]);

export function isFileFieldType(type: FieldType): boolean {
  return FILE_FIELD_TYPES.has(type);
}

export function serializeAnswersForApi(
  form: BuiltForm,
  answers: RespondentAnswers,
): ApiAnswerItem[] {
  const fields = answerFields(form);
  const out: ApiAnswerItem[] = [];

  for (const field of fields) {
    if (!(field.id in answers)) {
      continue;
    }
    const raw = answers[field.id];
    const value = serializeFieldValue(field, raw);
    if (value === undefined) {
      continue;
    }
    out.push({ fieldId: field.id, value });
  }

  return out;
}

export function collectPendingFiles(
  form: BuiltForm,
  answers: RespondentAnswers,
): Array<{ fieldId: string; files: File[] }> {
  const pending: Array<{ fieldId: string; files: File[] }> = [];
  for (const field of answerFields(form)) {
    if (!isFileFieldType(field.type)) {
      continue;
    }
    const raw = answers[field.id];
    if (!Array.isArray(raw)) {
      continue;
    }
    const files = raw.filter(
      (item): item is File => typeof File !== 'undefined' && item instanceof File,
    );
    if (files.length) {
      pending.push({ fieldId: field.id, files });
    }
  }
  return pending;
}

export function applyFileIdsToAnswers(
  answers: RespondentAnswers,
  fieldId: string,
  fileIds: string[],
): RespondentAnswers {
  return {
    ...answers,
    [fieldId]: { fileIds },
  };
}

function serializeFieldValue(field: FormField, raw: AnswerValue): unknown {
  if (raw === undefined) {
    return undefined;
  }
  if (raw === null) {
    return null;
  }

  if (isFileFieldType(field.type)) {
    if (
      typeof raw === 'object' &&
      !Array.isArray(raw) &&
      raw != null &&
      'fileIds' in raw &&
      Array.isArray((raw as { fileIds: unknown }).fileIds)
    ) {
      return {
        fileIds: (raw as { fileIds: string[] }).fileIds.filter(
          (id) => typeof id === 'string' && id.trim(),
        ),
      };
    }
    if (Array.isArray(raw)) {
      return undefined;
    }
    return null;
  }

  if (raw instanceof Date) {
    return raw.toISOString();
  }

  if (
    typeof raw === 'object' &&
    raw != null &&
    !Array.isArray(raw) &&
    'startDate' in raw &&
    'endDate' in raw
  ) {
    const range = raw as {
      startDate: Date | string | null;
      endDate: Date | string | null;
    };
    return {
      startDate: toIsoOrNull(range.startDate),
      endDate: toIsoOrNull(range.endDate),
    };
  }

  if (field.type === 'single_choice_grid') {
    return serializeSingleChoiceGrid(field, raw);
  }

  if (field.type === 'multi_choice_grid') {
    return serializeMultiChoiceGrid(field, raw);
  }

  if (field.type === 'number_grid') {
    return serializeNumberGrid(field, raw);
  }

  return raw;
}

function serializeSingleChoiceGrid(field: FormField, raw: AnswerValue): unknown {
  if (!Array.isArray(raw)) {
    return raw;
  }
  const labelById = gridChoiceLabelMap(field);
  return raw.map((row) => {
    if (!isPlainObject(row)) {
      return row;
    }
    const selectedId = readSelectedChoiceId(row);
    return {
      ...pickGridRowIdentity(row),
      selectedChoice: selectedId
        ? {
            id: selectedId,
            label: labelById.get(selectedId) ?? selectedId,
          }
        : null,
    };
  });
}

function serializeMultiChoiceGrid(field: FormField, raw: AnswerValue): unknown {
  if (!Array.isArray(raw)) {
    return raw;
  }
  const labelById = gridChoiceLabelMap(field);
  return raw.map((row) => {
    if (!isPlainObject(row)) {
      return row;
    }
    const selectedIds = readSelectedChoiceIds(row);
    return {
      ...pickGridRowIdentity(row),
      selectedChoices: selectedIds.map((id) => ({
        id,
        label: labelById.get(id) ?? id,
      })),
    };
  });
}

function serializeNumberGrid(field: FormField, raw: AnswerValue): unknown {
  if (!Array.isArray(raw)) {
    return raw;
  }
  const columns = numberGridColumns(field);
  return (raw as unknown[]).map((row) => {
    if (!isPlainObject(row)) {
      return row;
    }
    const existingValues = Array.isArray(row['values']) ? row['values'] : [];
    const valueById = new Map<string, number | null>();
    for (const entry of existingValues) {
      if (!isPlainObject(entry)) {
        continue;
      }
      const id =
        typeof entry['id'] === 'string' && entry['id'].trim()
          ? entry['id'].trim()
          : typeof entry['key'] === 'string' && entry['key'].trim()
            ? entry['key'].trim()
            : '';
      if (!id) {
        continue;
      }
      const rawValue = entry['value'];
      if (rawValue === null || rawValue === undefined || rawValue === '') {
        valueById.set(id, null);
        continue;
      }
      const n = typeof rawValue === 'number' ? rawValue : Number(rawValue);
      valueById.set(id, Number.isFinite(n) ? n : null);
    }

    const values =
      columns.length > 0
        ? columns.map((column) => ({
            id: column.id,
            label: column.label,
            value: valueById.has(column.id) ? valueById.get(column.id)! : null,
          }))
        : existingValues
            .map((entry) => {
              if (!isPlainObject(entry)) {
                return null;
              }
              const id =
                typeof entry['id'] === 'string' && entry['id'].trim()
                  ? entry['id'].trim()
                  : typeof entry['key'] === 'string' && entry['key'].trim()
                    ? entry['key'].trim()
                    : '';
              if (!id) {
                return null;
              }
              const label =
                typeof entry['label'] === 'string' ? entry['label'] : id;
              const rawValue = entry['value'];
              let value: number | null = null;
              if (
                rawValue !== null &&
                rawValue !== undefined &&
                rawValue !== ''
              ) {
                const n =
                  typeof rawValue === 'number' ? rawValue : Number(rawValue);
                value = Number.isFinite(n) ? n : null;
              }
              return { id, label, value };
            })
            .filter(
              (
                entry,
              ): entry is { id: string; label: string; value: number | null } =>
                entry != null,
            );

    return {
      ...pickGridRowIdentity(row),
      values,
    };
  });
}

function numberGridColumns(
  field: FormField,
): Array<{ id: string; label: string }> {
  const config = field.config as { columns?: unknown } | undefined;
  const raw = Array.isArray(config?.columns) ? config.columns : [];
  const out: Array<{ id: string; label: string }> = [];
  for (const item of raw) {
    if (!isPlainObject(item)) {
      continue;
    }
    const label = typeof item['label'] === 'string' ? item['label'] : '';
    const id =
      typeof item['id'] === 'string' && item['id'].trim()
        ? item['id'].trim()
        : typeof item['key'] === 'string' && item['key'].trim()
          ? item['key'].trim()
          : '';
    if (!id) {
      continue;
    }
    out.push({ id, label: label || id });
  }
  return out;
}

function gridChoiceLabelMap(field: FormField): Map<string, string> {
  const config = field.config as
    | { options?: unknown; choice?: unknown }
    | undefined;
  const choices = hydrateGridChoices(config?.options ?? config?.choice ?? []);
  return new Map(
    choices.map((choice) => [choice.id, choice.label.trim() || choice.id]),
  );
}

function pickGridRowIdentity(row: Record<string, unknown>): {
  id?: string;
  uniqueKey?: string;
  display: string;
} {
  const id = typeof row['id'] === 'string' ? row['id'].trim() : '';
  const uniqueKey =
    typeof row['uniqueKey'] === 'string' ? row['uniqueKey'].trim() : '';
  const display = typeof row['display'] === 'string' ? row['display'] : '';
  return {
    ...(id ? { id } : {}),
    ...(uniqueKey ? { uniqueKey } : {}),
    display,
  };
}

function readSelectedChoiceId(row: Record<string, unknown>): string | null {
  const fromId = row['selectedChoiceId'];
  if (typeof fromId === 'string' && fromId.trim()) {
    return fromId.trim();
  }
  const snapshot = row['selectedChoice'];
  if (isPlainObject(snapshot) && typeof snapshot['id'] === 'string') {
    const id = snapshot['id'].trim();
    return id || null;
  }
  if (typeof snapshot === 'string' && snapshot.trim()) {
    return snapshot.trim();
  }
  return null;
}

function readSelectedChoiceIds(row: Record<string, unknown>): string[] {
  const fromIds = row['selectedChoiceIds'];
  if (Array.isArray(fromIds)) {
    return fromIds
      .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
      .map((id) => id.trim());
  }
  const snapshots = row['selectedChoices'];
  if (!Array.isArray(snapshots)) {
    return [];
  }
  return snapshots
    .map((item) => {
      if (typeof item === 'string' && item.trim()) {
        return item.trim();
      }
      if (isPlainObject(item) && typeof item['id'] === 'string') {
        return item['id'].trim();
      }
      return '';
    })
    .filter(Boolean);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toIsoOrNull(value: Date | string | null | undefined): string | null {
  if (value == null || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return String(value);
}

export function mergeDraftAnswers(
  defaults: RespondentAnswers,
  draftAnswers: Array<{ fieldId: string; value: unknown }>,
): RespondentAnswers {
  const next: RespondentAnswers = { ...defaults };
  for (const entry of draftAnswers) {
    if (!entry?.fieldId) {
      continue;
    }
    next[entry.fieldId] = coerceDraftValue(entry.value) as AnswerValue;
  }
  return next;
}

function coerceDraftValue(value: unknown): unknown {
  if (value == null) {
    return value;
  }
  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    value != null &&
    'startDate' in value
  ) {
    const range = value as { startDate?: unknown; endDate?: unknown };
    return {
      startDate: parseMaybeDate(range.startDate),
      endDate: parseMaybeDate(range.endDate),
    };
  }
  if (Array.isArray(value)) {
    return value.map(coerceDraftGridRow);
  }
  return value;
}

/** Snapshotted API choice objects → live grid control string ids. */
function coerceDraftGridRow(row: unknown): unknown {
  if (!isPlainObject(row)) {
    return row;
  }

  const hasChoiceSnapshot =
    'selectedChoice' in row ||
    'selectedChoices' in row ||
    'selectedChoiceId' in row ||
    'selectedChoiceIds' in row;
  if (!hasChoiceSnapshot) {
    return row;
  }

  const next: Record<string, unknown> = { ...row };

  if ('selectedChoice' in next || 'selectedChoiceId' in next) {
    next['selectedChoiceId'] = readSelectedChoiceId(next);
    delete next['selectedChoice'];
  }

  if ('selectedChoices' in next || 'selectedChoiceIds' in next) {
    next['selectedChoiceIds'] = readSelectedChoiceIds(next);
    delete next['selectedChoices'];
  }

  return next;
}

function parseMaybeDate(value: unknown): Date | null {
  if (value == null || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
